import {
  areStoredExperimentsValid,
  clearExperimentsCookie,
  getStoredExperiments,
  setExperimentsCookie,
} from '@core/abTesting/backend/storage'
import type { BackendExperiment } from '@core/abTesting/backend/types'
import { getGlobalConfig } from '@core/globalConfig'
import { logInfo, logWarning } from '@core/logging/helpers'
import { getGuestId } from '@core/tracking/guestId'

import { fetchExperiments } from './api'
import { trackAbTest } from './tracking'

let isInitialized = false
let cachedExperiments: BackendExperiment[] | null = null

/**
 * Parses URL parameter overrides for experiments
 * Supports format: fwntests=experiment_type:variant_type,another_type:variant
 */
function parseUrlExperimentOverrides(): BackendExperiment[] | null {
  if (typeof window === 'undefined') {
    return []
  }

  const urlParams = new URLSearchParams(window.location.search)
  const fwntests = urlParams.get('fwntests')

  if (!fwntests) {
    return null
  }

  const overrides: BackendExperiment[] = []
  const experiments = fwntests.split(',')

  experiments.forEach((experiment) => {
    const [baseType, variantValue] = experiment.split(':')

    if (baseType && variantValue) {
      overrides.push({
        experiment_id: baseType.trim(),
        base_experiment_type: baseType.trim(),
        variant_value: variantValue.trim(),
        name: `URL Override: ${baseType}`,
      })

      logInfo(`URL parameter override: ${baseType} -> ${variantValue}`)
    }
  })

  return overrides
}

/**
 * Initializes the backend-driven A/B testing framework
 * This should be called during fwnInit initialization
 */
export async function initializeBackendABTesting(): Promise<void> {
  if (isInitialized) {
    logInfo('Backend A/B testing already initialized')
    return
  }

  try {
    const globalConfig = getGlobalConfig()
    const businessId = globalConfig.business_id

    if (!businessId) {
      logInfo(
        'No business_id found in global config, skipping backend A/B testing initialization',
      )
      return
    }

    logInfo(`Initializing backend A/B testing for business_id: ${businessId}`)

    // Parse URL parameter overrides
    const urlOverrides = parseUrlExperimentOverrides()
    if (urlOverrides) {
      cachedExperiments = urlOverrides
      logInfo(
        `Using URL parameter overrides: ${urlOverrides.length} experiments`,
      )
      isInitialized = true
      return
    }

    // Check if we have valid cached experiments
    if (areStoredExperimentsValid()) {
      const storedExperiments = getStoredExperiments()
      if (storedExperiments) {
        cachedExperiments = storedExperiments
        logInfo(
          `Using cached experiments: ${storedExperiments.length} experiments`,
        )
        isInitialized = true
        return
      }
    }

    //Fetch fresh experiments from backend
    const guestId = getGuestId()
    const experiments = await fetchExperiments({
      business_id: businessId,
      guestId,
    })

    logInfo('Experiments fetched from backend: ', experiments)

    if (experiments) {
      await setExperimentsCookie(experiments)
      cachedExperiments = experiments

      // Track experiments
      if (experiments.length > 0 && !urlOverrides) {
        trackAbTest(experiments)
      }

      logInfo(`Backend A/B testing initialized with ${experiments} experiments`)
    } else {
      logInfo(
        'No experiments received from backend - guest not participating in A/B tests',
      )
      clearExperimentsCookie()
    }

    isInitialized = true
  } catch (error) {
    logWarning(`Failed to initialize backend A/B testing: ${error}`)
    isInitialized = false
  }
}

/**
 * Gets the current experiments for this user
 */
export function getBackendExperiments(): BackendExperiment[] | null {
  if (!isInitialized) {
    logInfo(
      'Backend A/B testing not initialized. Call initializeBackendABTesting() first.',
    )
    return null
  }

  return cachedExperiments || null
}

/**
 * Gets a specific experiment by ID
 */
export function getExperimentById(
  experimentId: string,
): BackendExperiment | undefined {
  const experiments = getBackendExperiments()
  return experiments?.find((exp) => exp.experiment_id === experimentId)
}

/**
 * Gets a specific experiment by base experiment type
 */
export function getExperimentByType(
  baseExperimentType: string,
): BackendExperiment | undefined {
  const experiments = getBackendExperiments()
  return experiments?.find(
    (exp) => exp.base_experiment_type === baseExperimentType,
  )
}

/**
 * Checks if user is in a specific experiment variant by base experiment type
 */
export function isInExperimentVariant(
  baseExperimentType: string,
  variantType: string,
): boolean {
  const experiments = getBackendExperiments()
  if (experiments?.length === 0) {
    return false
  }

  const experiment = experiments?.find(
    (exp) => exp.base_experiment_type === baseExperimentType,
  )

  return experiment?.variant_value === variantType
}

/**
 * Checks if user is in the hide experiment variant
 */
export function isInEmbedVisibilityHideExperimentVariant(): boolean {
  logInfo('isInEmbedVisibilityHideExperimentVariant')
  const experiments = getBackendExperiments()

  if (experiments?.length === 0) {
    return true
  }

  const embedVisibilityExperiment = experiments?.find(
    (exp) => exp.base_experiment_type === 'embed_visibility',
  )
  return (
    embedVisibilityExperiment?.variant_value === 'hide' ||
    experiments?.length === 0
  )
}

/**
 * Checks if user is in the show experiment variant
 */
export function isInEmbedVisibilityShowExperimentVariant(): boolean {
  logInfo('isInEmbedVisibilityShowExperimentVariant')
  return isInExperimentVariant('embed_visibility', 'show')
}

/**
 * Checks if user is in the firework visibility hide experiment variant
 */
export function isInFireworkVisibilityHideExperimentVariant(): boolean {
  logInfo('isInFireworkVisibilityHideExperimentVariant')
  const experiments = getBackendExperiments()

  if (experiments?.length === 0) {
    return true
  }

  const embedVisibilityExperiment = experiments?.find(
    (exp) => exp.base_experiment_type === 'firework_visibility',
  )
  return (
    embedVisibilityExperiment?.variant_value === 'hide' ||
    experiments?.length === 0
  )
}

/**
 * Checks if user is in the show experiment variant
 */
export function isInFireworkVisibilityShowExperimentVariant(): boolean {
  logInfo('isInFireworkVisibilityShowExperimentVariant')
  return isInExperimentVariant('firework_visibility', 'show')
}

/**
 * Gets all experiments formatted for tracking
 * Compatible with existing tracking system
 */
export function getBackendExperimentsForTracking(): BackendExperiment[] | null {
  const experiments = getBackendExperiments()
  // If there are URL overrides, don't return the experiments
  if (experiments?.some((exp) => exp.name.includes('URL Override'))) {
    return null
  }

  return experiments || null
}

/**
 * Forces a refresh of experiments from the backend
 * Useful for testing or when business context changes
 */
export async function refreshExperiments(): Promise<void> {
  logInfo('Forcing refresh of backend experiments')

  isInitialized = false
  cachedExperiments = null
  clearExperimentsCookie()

  await initializeBackendABTesting()
}

/**
 * Resets the backend A/B testing system
 * Useful for testing or cleanup
 */
export function resetBackendABTesting(): void {
  isInitialized = false
  cachedExperiments = null
  clearExperimentsCookie()
  logInfo('Backend A/B testing system reset')
}
